<template>
  <component :is="compiledComponent" v-if="compiledComponent" :data-name="name" />
</template>

<script setup>
import { compile, defineComponent, shallowRef, watch } from 'vue'
import DOMPurify from 'dompurify'

import { getSiteContent } from '@shared/api/systemData.api'
import GuAlert from '@shared/gu-components/alert/GuAlert.vue'
import GuBadge from '@shared/gu-components/badge/GuBadge.vue'
import GuLink from '@shared/gu-components/link/GuLink.vue'

const props = defineProps({
  name: {
    type: String,
    required: true,
  },
  queryOptions: {
    type: Object,
    default: () => ({}),
  },
  contentType: {
    type: String,
    default: 'text/html',
  },
})

const { name, queryOptions, contentType } = props

const { data: siteContent } = getSiteContent(name, {
  retry: false,
  ...queryOptions,
}, contentType)

const compiledComponent = shallowRef(null)

watch(siteContent, (newContent) => {
  if (newContent) {
    const sanitizedContent = DOMPurify.sanitize(newContent, {
      FORBID_TAGS: ['script', 'style'],
      ADD_TAGS: ['GuAlert', 'GuBadge', 'GuLink'],
      ADD_ATTR: ['class', 'disabled', 'dismissable', 'href', 'icon', 'iconPos', 'outline', 'pill', 'size', 'to', 'variant'],
    })

    const renderFunction = compile(sanitizedContent)

    // Prepare components object with both original and lowercased names
    // since DomPurify makes them lowercase on the dom like <gualert>
    //! Use this list to add more components that the users can add to the overrides
    const componentsList = { GuAlert, GuBadge, GuLink }

    const components = {}
    Object.entries(componentsList).forEach(([name, component]) => {
      components[name] = component // Original name (e.g., 'GuAlert')
      components[name.toLowerCase()] = component // Lowercased name (e.g., 'gualert')
    })

    compiledComponent.value = defineComponent({
      components,
      render: renderFunction,
    })
  } else {
    compiledComponent.value = null
  }
})
</script>
